import { useEffect, useState, type ReactNode } from 'react';
import { FILTER_NO_AREA } from '../areas-report';
import { FILTER_NOBODY, SORT_LABEL, nothingOn, toggle, type FilterField, type Filters, type SortBy } from '../filters';
import { FIELD_LABEL, FIELDS, passes, type Field, type View } from '../views';
import { liveTypes, type IssueType } from '../issue-types';
import { FILTER_NO_PHASE, livePhases, type Phase } from '../phases';
import type { Area, Issue, IssueStatus } from '../types';
import { PRIORITY_LABELS, PRIORITY_ORDER, SIZE_LABEL, SIZE_ORDER, STATUS_LABELS, STATUS_ORDER } from '../types';

/**
 * THE BACKLOG'S FILTER BAR, REBUILT — #301.
 *
 * WHAT WAS WRONG. Thirteen controls, drawn at full strength whether or not any
 * of them were in use, on a board whose ordinary state is "nothing filtered".
 * Vinnie, 26 August 2026: *"This section is getting very overwhelming to look
 * at. There has to be a better way."*
 *
 * ⚠️ AND IT WAS A DEFECT, NOT ONLY CLUTTER. Three kinds of control were sitting
 * there as equals and they are not equal. Status, Epic, Size and Area **hide
 * issues** — get one wrong and work you needed is missing. Group by and Sort
 * hide nothing; they only redraw the same set. Reset is an escape hatch, drawn
 * whether or not there is anything to escape. A select reading "Any size" and a
 * greyed-out chip are both easy to miss, so **issues vanished from the backlog
 * with nothing on screen accounting for it.**
 *
 * ── THE SHAPE, from dev-preview/backlog-filters-v1.html, approved 30 August ──
 *
 *   A row of views, carrying the counts the status chips used to carry.
 *   A `Filter` button — one searchable menu, field first, then values.
 *   A `Display` button — Group by and Sort, which hide nothing.
 *   A pill per choice, naming the field and the value IN WORDS, each removable.
 *   A count line saying how many are hidden and why.
 *
 * Resting state: two buttons instead of thirteen controls. Nothing is taken
 * away — and Priority, Type and Assignee are added, which the old row could not
 * have absorbed without getting worse.
 *
 * ⛔ TWO ARRANGEMENTS WERE DRAWN AND REJECTED, so nobody re-runs that ground.
 * Collapsing the row behind "Filters (2)" is the smallest change but hides
 * WHICH filters are on, so the missing-work problem survives it. Search syntax
 * — `is:open area:ui` — is fast once learned, but it has to be learned and
 * offers nothing to point at: wrong as the only way in for TRINRE's people,
 * worth adding alongside one day.
 *
 * DRAWN SEPARATELY FROM THE BACKLOG, and that is not tidiness. Every menu here
 * opens on a click, and the screen harness cannot click — so inline, the menus
 * would be the one part of the app nothing ever renders and nobody reviews.
 * That is exactly how a blank badge survived in History for weeks.
 */

/** A field's values, in the order they should be offered, with their words. */
export interface Choice {
  value: string;
  label: string;
}

export function choicesFor(
  field: Field,
  issues: Issue[],
  areas: Area[],
  people: string[],
  nameOf: (email: string) => string,
  noEpic: string,
  types: IssueType[] = [],
  phases: Phase[] = []
): Choice[] {
  switch (field) {
    case 'status':
      /* In Staging is offered as a filter only once a card is actually in it —
         which is exactly the boards that turned the column on. Every other
         status is always listed, as before. */
      return STATUS_ORDER.filter(
        (s) => s !== 'staging' || issues.some((i) => i.status === 'staging')
      ).map((s) => ({ value: s, label: STATUS_LABELS[s as IssueStatus] }));
    case 'area':
      return [
        ...areas.filter((a) => !a.retiredAt).map((a) => ({ value: a.id, label: a.name })),
        /* "No area" LAST AND ALWAYS PRESENT. 244 of this board's cards have
           none, so it is the largest single answer and not an edge case. */
        { value: FILTER_NO_AREA, label: 'No area yet' },
      ];
    case 'phase':
      /* THE STAGES, IN THE ORDER THE PLAN RUNS THEM — #377, WS-4. Same shape as
         area above: the live phases first, then a "No phase" bucket for the many
         cards not yet placed in one. A retired phase still in use stays
         filterable, exactly as a retired type does, so the cards that carry it
         are not left unreachable. */
      return [
        ...livePhases(phases).map((p) => ({ value: p.id, label: p.name })),
        ...phases
          .filter((p) => p.retiredAt && issues.some((i) => i.phase === p.id))
          .map((p) => ({ value: p.id, label: `${p.name} (retired)` })),
        { value: FILTER_NO_PHASE, label: 'No phase yet' },
      ];
    case 'epic':
      return [...new Set(issues.map((i) => i.epic || noEpic))].sort().map((n) => ({ value: n, label: n }));
    case 'size':
      return SIZE_ORDER.map((s) => ({ value: s, label: SIZE_LABEL[s] }));
    case 'priority':
      return PRIORITY_ORDER.map((p) => ({ value: p, label: PRIORITY_LABELS[p] }));
    case 'type':
      /*
       * THE BOARD'S OWN TYPES, IN THE BOARD'S ORDER — #302. This offered the
       * four fixed words, so a board calling them something else got a filter
       * menu in a vocabulary nobody there uses.
       *
       * ⚠️ AND ANY RETIRED TYPE STILL IN USE. `liveTypes` alone would leave
       * work unfilterable: retiring a type keeps the cards that have it, and
       * those cards are exactly the ones somebody is looking for when they go
       * hunting for what the old classification covered.
       */
      return [
        ...liveTypes(types).map((t) => ({ value: t.slug, label: t.name })),
        ...types
          .filter((t) => t.retiredAt && issues.some((i) => i.kind === t.slug))
          .map((t) => ({ value: t.slug, label: `${t.name} (retired)` })),
      ];
    case 'assignee':
      return [
        ...people.map((e) => ({ value: e, label: nameOf(e) })),
        /* The thing a planning session is actually looking for. */
        { value: FILTER_NOBODY, label: 'Nobody yet' },
      ];
    case 'responsible':
      /* Whoever actually holds a card as its Responsible (two-part assignee).
         Derived from the cards in view, like the Accountable list, so only people
         with work here appear; "Nobody yet" is the set a planner hunts. */
      return [
        ...[...new Set(issues.map((i) => i.responsible).filter((e): e is string => !!e))]
          .sort((a, b) => nameOf(a).localeCompare(nameOf(b)))
          .map((e) => ({ value: e, label: nameOf(e) })),
        { value: FILTER_NOBODY, label: 'Nobody yet' },
      ];
  }
}

/**
 * HOW MANY CARDS A VALUE WOULD LEAVE, counted with every OTHER field's filters
 * applied but not this one's.
 *
 * That is the count that answers the question somebody is holding while the
 * menu is open — "how many if I pick this" — rather than "how many are there in
 * the whole world", which is true and useless once anything else is on.
 */
export function countFor(
  field: Field,
  value: string,
  inThisView: Issue[],
  filters: Filters,
  noEpic: string
): number {
  const without = { ...filters, [field]: [] } as Filters;
  const only = { ...DEFAULT_EMPTY, [field]: [value] } as Filters;
  return inThisView.filter((i) => passes(i, without, noEpic) && passes(i, only, noEpic)).length;
}

const DEFAULT_EMPTY: Filters = {
  status: [], area: [], phase: [], epic: [], size: [], priority: [], type: [], assignee: [], responsible: [], sort: 'updated',
};

export type GroupBy = 'status' | 'epic' | 'area' | 'phase' | 'none';

export function BacklogFilters({
  issues,
  inThisView,
  areas,
  types,
  phases,
  people,
  nameOf,
  noEpic,
  filters,
  onFilters,
  views,
  viewId,
  onView,
  countInView,
  groupBy,
  onGroupBy,
  showCounts,
  onShowCounts,
  onSaveView,
  onForgetView,
  shown,
  hiddenByFilters,
  hidesDone,
  /** Draw both menus open. For screen checks, which cannot click. */
  openMenus,
  /**
   * Whether the Display button is offered. True on the Backlog, where Display
   * carries group-by and the per-group counts. False in the Grid, which groups
   * nothing and sorts from its own column headers — there the button would open
   * a menu of choices that do not apply (#508). The saved-view tabs and Filter
   * are shared in both.
   */
  showDisplay = true,
  /** An optional control rendered inline at the start of the button group, just
   *  before Filter. The Grid passes its sprint picker here so it sits on the
   *  same line as Filter rather than on a row of its own (#508). */
  extra,
}: {
  issues: Issue[];
  inThisView: Issue[];
  areas: Area[];
  /** The board's own types, for the Type menu — #302. */
  types: IssueType[];
  /** The project's stages, for the Phase menu and group-by — #377, WS-4. */
  phases: Phase[];
  people: string[];
  nameOf: (email: string) => string;
  noEpic: string;
  filters: Filters;
  onFilters: (f: Filters) => void;
  views: View[];
  viewId: string;
  onView: (id: string) => void;
  countInView: (v: View) => number;
  groupBy: GroupBy;
  onGroupBy: (g: GroupBy) => void;
  showCounts: boolean;
  onShowCounts: (on: boolean) => void;
  onSaveView?: (name: string) => void;
  onForgetView?: (id: string) => void;
  shown: number;
  hiddenByFilters: number;
  hidesDone: boolean;
  openMenus?: boolean;
  showDisplay?: boolean;
  extra?: ReactNode;
}) {
  const [menu, setMenu] = useState<'' | 'filter' | 'display'>(openMenus ? 'filter' : '');

  /**
   * ── HOW A MENU CLOSES ────────────────────────────────────────────────────
   *
   * Pressing the button again was the only way out, which the browser walk
   * found: the menu sat over the right-hand third of the rows, and clicking a
   * row underneath it — the obvious thing to do next — did nothing at all.
   *
   * So: anywhere outside closes it, and so does Escape. NOT on choosing a
   * value, which is deliberate — picking three sizes is one action, and a menu
   * that shut after the first would have to be reopened twice to finish it.
   */
  useEffect(() => {
    if (!menu || openMenus) return;
    /*
     * ⚠️ THE MENU AND ITS BUTTONS, NOT THE WHOLE BAR — Vinnie, 2 September
     * 2026: *"when clicking away from it, it doesn't do anything, it is
     * supposed to close if I click away from it"*.
     *
     * This asked whether the click was inside `.bfilters`, which is the ref
     * used for POSITIONING — a full-width strip across the top of the backlog,
     * measured at 1012×68 on his screen. The Filter and Display buttons sit at
     * its right-hand end, so the nearest empty space to an open menu, and the
     * first place anybody clicks to dismiss it, was still "inside" and the
     * click did nothing at all. Clicking a row far below did close it, which is
     * why this survived a walk.
     *
     * The two things a click may land on without dismissing are the menu itself
     * and the buttons that opened it. Everything else is away. Asked of the
     * ELEMENTS rather than of the container they happen to sit in, so moving
     * either of them in the markup cannot quietly widen this again.
     */
    const away = (e: MouseEvent) => {
      const hit = e.target as Element | null;
      if (!hit?.closest?.('.bf-menu, .bf-buttons')) setMenu('');
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu('');
    };
    /* On mousedown rather than click: a click that starts inside the menu and
       ends outside it — selecting the text in the search box and letting go
       past the edge — is not somebody dismissing anything. */
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [menu, openMenus]);
  const [field, setField] = useState<Field | ''>(openMenus ? 'area' : '');
  const [look, setLook] = useState('');
  const [naming, setNaming] = useState(false);
  const [newName, setNewName] = useState('');

  const on = (f: Field) => (filters[f as FilterField] as string[]) ?? [];
  const label = (f: Field, v: string) =>
    choicesFor(f, issues, areas, people, nameOf, noEpic, types, phases).find((c) => c.value === v)?.label ??
    /* A value whose thing has since been removed still has to read as
       something. Silence here is a pill nobody can interpret or trust. */
    'one that is no longer here';

  return (
    <div className="bfilters">
      {/* ── THE VIEWS, carrying the counts the status chips used to ─────── */}
      <div className="bf-views" role="tablist" aria-label="Views">
        {views.map((v) => (
          <span key={v.id} className="bf-view-wrap">
            <button
              role="tab"
              aria-selected={v.id === viewId}
              className={`bf-view${v.id === viewId ? ' bf-on' : ''}`}
              onClick={() => onView(v.id)}
            >
              {v.name}
              <span className="bf-view-n">{countInView(v)}</span>
            </button>
            {/* Only a saved one can be forgotten. The four that come with the
                board are not somebody's to remove. */}
            {v.filters && onForgetView && (
              <button
                className="bf-view-x"
                aria-label={`Forget the view ${v.name}`}
                onClick={() => onForgetView(v.id)}
              >
                ×
              </button>
            )}
          </span>
        ))}
        {onSaveView && (
          <button
            className="bf-add"
            aria-label="Save these filters as a view"
            title="Save these filters as a view"
            onClick={() => setNaming(true)}
          >
            +
          </button>
        )}

        <div className="bf-buttons">
          {extra}
          <button
            className={`tb-btn${menu === 'filter' ? ' open' : ''}`}
            aria-expanded={menu === 'filter'}
            onClick={() => setMenu(menu === 'filter' ? '' : 'filter')}
          >
            Filter
            {!nothingOn(filters) && <span className="bf-n">{FIELDS.filter((f) => on(f).length).length}</span>}
          </button>
          {showDisplay && (
            <button
              className={`tb-btn${menu === 'display' ? ' open' : ''}`}
              aria-expanded={menu === 'display'}
              onClick={() => setMenu(menu === 'display' ? '' : 'display')}
            >
              Display
            </button>
          )}
        </div>
      </div>

      {naming && (
        <form
          className="bf-naming"
          onSubmit={(e) => {
            e.preventDefault();
            if (!newName.trim()) return;
            onSaveView?.(newName.trim());
            setNewName('');
            setNaming(false);
          }}
        >
          <input
            className="input"
            autoFocus
            value={newName}
            placeholder="Name this view"
            aria-label="Name this view"
            onChange={(e) => setNewName(e.target.value)}
          />
          <button className="btn btn-small btn-primary" type="submit">
            Save view
          </button>
          <button className="btn btn-small" type="button" onClick={() => setNaming(false)}>
            Cancel
          </button>
        </form>
      )}

      {/* ── FILTER: the field first, then its values ───────────────────── */}
      {menu === 'filter' && (
        <div className="bf-menu" role="dialog" aria-label="Filter">
          {!field ? (
            <>
              <p className="bf-menu-head">Filter by…</p>
              <ul className="bf-fields">
                {FIELDS.map((f) => (
                  <li key={f}>
                    <button onClick={() => { setField(f); setLook(''); }}>
                      {FIELD_LABEL[f]}
                      {on(f).length > 0 && <span className="bf-n">{on(f).length}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <p className="bf-menu-head">
                <button className="bf-back" onClick={() => setField('')}>
                  ‹ Back
                </button>
                {FIELD_LABEL[field]}
              </p>
              {/* Searchable, so it stays usable at four values or forty. */}
              <input
                className="bf-look"
                value={look}
                placeholder={`Search ${FIELD_LABEL[field].toLowerCase()}…`}
                aria-label={`Search ${FIELD_LABEL[field].toLowerCase()}`}
                onChange={(e) => setLook(e.target.value)}
              />
              <ul className="bf-values">
                {choicesFor(field, issues, areas, people, nameOf, noEpic, types, phases)
                  .filter((c) => c.label.toLowerCase().includes(look.trim().toLowerCase()))
                  .map((c) => (
                    <li key={c.value}>
                      <button
                        aria-pressed={on(field).includes(c.value)}
                        onClick={() => onFilters(toggle(filters, field as FilterField, c.value))}
                      >
                        <span className="bf-tick" aria-hidden="true">
                          {on(field).includes(c.value) ? '✓' : ''}
                        </span>
                        {c.label}
                        {/* THE COUNT NEXT TO THE VALUE IT DESCRIBES, which is
                            where it actually helps somebody decide. */}
                        <span className="bf-n">{countFor(field, c.value, inThisView, filters, noEpic)}</span>
                      </button>
                    </li>
                  ))}
              </ul>
            </>
          )}
        </div>
      )}

      {/* ── DISPLAY: nothing in here hides an issue ────────────────────── */}
      {(menu === 'display' || openMenus) && (
        <div className="bf-menu bf-display" role="dialog" aria-label="Display">
          <p className="bf-menu-head">Group by</p>
          <ul className="bf-values">
            {(['status', 'epic', 'area', 'phase', 'none'] as GroupBy[]).map((g) => (
              <li key={g}>
                <button aria-pressed={groupBy === g} onClick={() => onGroupBy(g)}>
                  <span className="bf-tick" aria-hidden="true">{groupBy === g ? '✓' : ''}</span>
                  {g === 'none' ? 'None' : g[0].toUpperCase() + g.slice(1)}
                </button>
              </li>
            ))}
          </ul>
          <p className="bf-menu-head">Sort</p>
          <ul className="bf-values">
            {(Object.keys(SORT_LABEL) as SortBy[]).map((k) => (
              <li key={k}>
                <button
                  aria-pressed={filters.sort === k}
                  onClick={() => onFilters({ ...filters, sort: k })}
                >
                  <span className="bf-tick" aria-hidden="true">{filters.sort === k ? '✓' : ''}</span>
                  {SORT_LABEL[k]}
                </button>
              </li>
            ))}
          </ul>
          <label className="bf-counts">
            <input type="checkbox" checked={showCounts} onChange={(e) => onShowCounts(e.target.checked)} />
            Show counts on groups
          </label>
          {/* Said out loud, because the whole reason this menu is separate is
              that people could not tell which controls were safe. */}
          <p className="bf-safe">Nothing here hides an issue.</p>
        </div>
      )}

      {/* ── THE PILLS: what is on, in words, each removable ────────────── */}
      {!nothingOn(filters) && (
        <div className="bf-pills">
          {FIELDS.filter((f) => on(f).length > 0).map((f) => (
            <span key={f} className="bf-pill">
              <span className="bf-pill-k">{FIELD_LABEL[f]}</span>
              {/* "UI or Workflow" — the word, because a pill reading "2" is the
                  greyed-out chip again in a different shape. */}
              <span className="bf-pill-v">{on(f).map((v) => label(f, v)).join(' or ')}</span>
              <button
                className="bf-pill-x"
                aria-label={`Stop filtering by ${FIELD_LABEL[f].toLowerCase()}`}
                onClick={() => onFilters({ ...filters, [f]: [] } as Filters)}
              >
                ×
              </button>
            </span>
          ))}
          <button className="link bf-clear" onClick={() => onFilters({ ...DEFAULT_EMPTY, sort: filters.sort })}>
            Clear filters
          </button>
        </div>
      )}

      {/* ── AND WHAT IS MISSING, AND WHY ───────────────────────────────── */}
      {(hiddenByFilters > 0 || hidesDone) && (
        <p className="bf-said">
          {shown} shown
          {hiddenByFilters > 0 && <> · {hiddenByFilters} hidden by these filters</>}
          {/* THE SENTENCE THAT REPLACED A GREYED-OUT CHIP. Done being hidden
              was true and invisible; the view says it. */}
          {hidesDone && <> · Done is hidden by this view</>}
        </p>
      )}
    </div>
  );
}
